import { Routes, Route, Navigate } from 'react-router-dom'
import { Toaster } from '@/components/ui/toaster'
import { FreemiumNudgeProvider } from '@/components/FreemiumNudgeProvider'
import { AuthProvider } from '@/contexts/AuthContext'
import { BackgroundJobsProvider } from '@/contexts/BackgroundJobsContext'
import { Layout } from '@/components/Layout'
import { ProtectedRoute } from '@/components/ProtectedRoute'

// Pages
import Landing from '@/pages/Landing'
import LandingNew from '@/pages/LandingNew'
import Training from '@/pages/Training'
import Login from '@/pages/Login'
import Register from '@/pages/Register'
import ForgotPassword from '@/pages/ForgotPassword'
import ResetPassword from '@/pages/ResetPassword'
import VerifyEmail from '@/pages/VerifyEmail'
import Approve from '@/pages/Approve'
import Dashboard from '@/pages/Dashboard'
import PrivacyPolicy from '@/pages/PrivacyPolicy'
import TermsOfService from '@/pages/TermsOfService'
import SecurityPolicy from '@/pages/SecurityPolicy'
import CompliancePolicy from '@/pages/CompliancePolicy'
import APIDocumentation from '@/pages/APIDocumentation'
import APIDocs from '@/pages/APIDocs'
import XInvestigation from '@/pages/XInvestigation'
import SocialIntelligence from '@/pages/SocialIntelligence'
import TelescopeMonitoring from '@/pages/TelescopeMonitoring'
import BreachINT from '@/pages/BreachINT'
import GEOINT_Wayback from '@/pages/GEOINT_Wayback'
import GEOINT_Maritime from '@/pages/GEOINT_Maritime'
import AirMonitoring from '@/pages/AirMonitoring'
import WiFiIntelligence from '@/pages/WiFiIntelligence'
import AdminLayout from '@/pages/admin/AdminLayout'
import OverviewSection from '@/pages/admin/OverviewSection'
import UsersSection from '@/pages/admin/UsersSection'
import OrganizationsSection from '@/pages/admin/OrganizationsSection'
import CreditsSection from '@/pages/admin/CreditsSection'
import SubscriptionsSection from '@/pages/admin/SubscriptionsSection'
import EmailsSection from '@/pages/admin/EmailsSection'
import LogsAuditSection from '@/pages/admin/LogsAuditSection'
import AMLSection from '@/pages/admin/AMLSection'
import SystemRulesSection from '@/pages/admin/SystemRulesSection'
import TelegramSection from '@/pages/admin/TelegramSection'
import StreamsSection from '@/pages/admin/StreamsSection'
import TwitterSection from '@/pages/admin/TwitterSection'
import AssistantSection from '@/pages/admin/AssistantSection'
import Profile from '@/pages/Profile'
import Organizations from '@/pages/Organizations'
import Subscription from '@/pages/Subscription'
import FacialRecognition from '@/pages/FacialRecognition'
import CyberTerrorism from '@/pages/CyberTerrorism'
import Playground from '@/pages/Playground'
import MonitorDashboard from '@/pages/MonitorDashboard'
import DeveloperPortal from '@/pages/DeveloperPortal'
import IndustryPage from '@/pages/IndustryPage'
import UseCases from '@/pages/UseCases'
import Pricing from '@/pages/Pricing'
import ProfilerIndex from '@/pages/profiler/index'
import CDDResults from '@/pages/profiler/cdd-results'
import EDDPipeline from '@/pages/profiler/edd-pipeline'
import DossierPage from '@/pages/profiler/dossier'
import CaseHistory from '@/pages/profiler/case-history'
import ApiKeys from '@/pages/profiler/api-keys'
import ProfilerSettings from '@/pages/profiler/settings'
import VerifyPage from '@/pages/profiler/verify'
import AMLScreening from '@/pages/AMLScreening'
import SycekProfiler from '@/pages/SycekProfiler'
import XInvestigationProduct from '@/pages/XInvestigationProduct'
import AirMaritimeProduct from '@/pages/AirMaritimeProduct'
import SocialStreamProduct from '@/pages/SocialStreamProduct'
import GEOINTProduct from '@/pages/GEOINTProduct'
import ModulePage from '@/pages/ModulePage'
import LearnIndex from '@/pages/LearnIndex'
import LearnArticle from '@/pages/LearnArticle'
import PreviewResults from '@/pages/PreviewResults'
import WalletIntelDashboard from '@/pages/wallet-intel/index'
import WalletProfile from '@/pages/wallet-intel/lookup'
import BatchScreening from '@/pages/wallet-intel/batch'
import SarExport from '@/pages/wallet-intel/reports'
import WalletAuditTrail from '@/pages/wallet-intel/audit'
import WalletIntelSettings from '@/pages/wallet-intel/settings'
import WatchlistDashboard from '@/pages/watchlist/index'
import WatchlistKeywords from '@/pages/watchlist/keywords'
import WatchlistProfiles from '@/pages/watchlist/profiles'
import WatchlistImpersonation from '@/pages/watchlist/impersonation'
import WatchlistOperators from '@/pages/watchlist/operators'
import WatchlistTakedowns from '@/pages/watchlist/takedowns'
import WatchlistSources from '@/pages/watchlist/sources'

function App() {
  return (
    <AuthProvider>
    <BackgroundJobsProvider>
      <Routes>
        <Route path="/" element={<LandingNew />} />
        <Route path="/landing-old" element={<Landing />} />
        <Route path="/training" element={<Training />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/approve" element={<Approve />} />
        <Route path="/monitor" element={<MonitorDashboard />} />
        {/* Legacy email deep-link redirects — old notifications pointed at /geoint-wayback without /app prefix */}
        <Route path="/geoint-wayback" element={<Navigate to={`/app/geoint-wayback${window.location.search}`} replace />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="/security" element={<SecurityPolicy />} />
        <Route path="/compliance" element={<CompliancePolicy />} />
        <Route path="/api-docs" element={<APIDocs />} />
        <Route path="/industries/:slug" element={<IndustryPage />} />
        <Route path="/use-cases" element={<UseCases />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/aml-profiler" element={<AMLScreening />} />
        <Route path="/aml-screening" element={<Navigate to="/aml-profiler" replace />} />
        <Route path="/sycek-profiler" element={<SycekProfiler />} />
        <Route path="/x-investigation-product" element={<XInvestigationProduct />} />
        <Route path="/air-maritime" element={<AirMaritimeProduct />} />
        <Route path="/social-stream-product" element={<SocialStreamProduct />} />
        <Route path="/geoint" element={<GEOINTProduct />} />
        <Route path="/geoint-product" element={<Navigate to="/geoint" replace />} />
        <Route path="/breachint" element={<ModulePage slug="breachint" />} />
        <Route path="/walletint" element={<ModulePage slug="walletint" />} />
        <Route path="/socint" element={<ModulePage slug="socint" />} />
        <Route path="/learn" element={<LearnIndex />} />
        <Route path="/learn/:slug" element={<LearnArticle />} />
        <Route path="/preview-results" element={<PreviewResults />} />
        <Route path="/api-docs-old" element={
          <ProtectedRoute>
            <APIDocumentation />
          </ProtectedRoute>
        } />
        <Route path="/app" element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }>
          <Route index element={<Navigate to="/app/dashboard" replace />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="x-investigation" element={<XInvestigation />} />
          <Route path="social-stream" element={<SocialIntelligence />} />
          <Route path="enhanced-social-stream" element={<Navigate to="/app/social-stream" replace />} />
          <Route path="telescope-monitoring" element={<TelescopeMonitoring />} />
          <Route path="breach" element={<BreachINT />} />
          <Route path="geoint-wayback" element={<GEOINT_Wayback />} />
          <Route path="geoint-maritime" element={<GEOINT_Maritime />} />
          <Route path="air-monitoring" element={<AirMonitoring />} />
          <Route path="wifi-intelligence" element={<WiFiIntelligence />} />
          <Route path="facial-recognition" element={<FacialRecognition />} />
          <Route path="cyber-terrorism" element={<CyberTerrorism />} />
          <Route path="playground" element={<Playground />} />
          <Route path="monitor" element={<MonitorDashboard />} />
          <Route path="admin" element={<AdminLayout />}>
            <Route index element={<Navigate to="overview" replace />} />
            <Route path="overview" element={<OverviewSection />} />
            <Route path="users" element={<UsersSection />} />
            <Route path="organizations" element={<OrganizationsSection />} />
            <Route path="credits" element={<CreditsSection />} />
            <Route path="subscriptions" element={<SubscriptionsSection />} />
            <Route path="emails" element={<EmailsSection />} />
            <Route path="logs" element={<LogsAuditSection />} />
            <Route path="aml" element={<AMLSection />} />
            <Route path="system" element={<SystemRulesSection />} />
            <Route path="streams" element={<StreamsSection />} />
            <Route path="telegram" element={<TelegramSection />} />
            <Route path="twitter" element={<TwitterSection />} />
            <Route path="assistant" element={<AssistantSection />} />
          </Route>
          <Route path="profile" element={<Profile />} />
          <Route path="organizations" element={<Organizations />} />
          <Route path="subscription" element={<Subscription />} />
          <Route path="developer" element={<DeveloperPortal />} />
          <Route path="profiler" element={<ProfilerIndex />} />
          <Route path="profiler/cases" element={<CaseHistory />} />
          <Route path="profiler/api-keys" element={<ApiKeys />} />
          <Route path="profiler/settings" element={<ProfilerSettings />} />
          <Route path="profiler/case/:caseId" element={<Navigate to="cdd" replace />} />
          <Route path="profiler/case/:caseId/cdd" element={<CDDResults />} />
          <Route path="profiler/case/:caseId/edd" element={<EDDPipeline />} />
          <Route path="profiler/case/:caseId/dossier" element={<DossierPage />} />
          <Route path="profiler/verify" element={<VerifyPage />} />
          <Route path="wallet-intel" element={<WalletIntelDashboard />} />
          <Route path="wallet-intel/lookup" element={<WalletProfile />} />
          <Route path="wallet-intel/batch" element={<BatchScreening />} />
          <Route path="wallet-intel/reports" element={<SarExport />} />
          <Route path="wallet-intel/audit" element={<WalletAuditTrail />} />
          <Route path="wallet-intel/settings" element={<WalletIntelSettings />} />
          <Route path="watchlist" element={<WatchlistDashboard />} />
          <Route path="watchlist/profiles" element={<WatchlistProfiles />} />
          <Route path="watchlist/brands" element={<Navigate to="/app/watchlist/profiles" replace />} />
          <Route path="watchlist/vips" element={<Navigate to="/app/watchlist/profiles?type=vip" replace />} />
          <Route path="watchlist/keywords" element={<WatchlistKeywords />} />
          <Route path="watchlist/impersonation" element={<WatchlistImpersonation />} />
          <Route path="watchlist/operators" element={<WatchlistOperators />} />
          <Route path="watchlist/takedowns" element={<WatchlistTakedowns />} />
          <Route path="watchlist/sources" element={<WatchlistSources />} />
        </Route>
      </Routes>
      <Toaster />
      <FreemiumNudgeProvider />
    </BackgroundJobsProvider>
    </AuthProvider>
  )
}

export default App